import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import PropTypes from 'prop-types';
import { List } from 'antd';
import { StyledTable } from '../../../components';
import TableLoadingComponent from '../../../components/Table/TableLoadingComponent';
import { getGivenConsents } from '../../../store/selectors/carriers.selectors';
import { isFeatureEnabled } from '../../../store/selectors';
import { FeatureToggle } from '../../../store/actions';
import RecipientInfo from './RecipientInfo';
import PredefinedLocationAccessCell from './PredefinedLocationAccessCell';

const GivenConsentsTable = ({ size }) => {
  const { t } = useTranslation();
  const givenConsents = useSelector(getGivenConsents);
  const isPredefinedLocationConsentEnabled = useSelector(
    isFeatureEnabled(FeatureToggle.PREDEFINED_LOCATION_CONSENT_ENABLED),
  );
  const predefinedType = 'PREDEFINED_LOCATION_MONITORING';
  const hiddenTypes = [predefinedType];
  const [windowHeight, setWindowHeight] = useState(window.innerHeight);

  const getConsentsKey = (consents) => (
    (consents || [])
      .map((consent) => `${consent.type || ''}:${consent.id || ''}:${consent.createdAt || ''}`)
      .join('|')
  );

  const renderType = (type) => (
    <List.Item style={{ display: 'block' }}>
      {type}
    </List.Item>
  );

  const renderTypes = (consents) => {
    const filtered = consents && consents.filter((consent) => !hiddenTypes.includes(consent.type));
    return filtered && filtered.length ? (
      <List
        dataSource={filtered.map((consent) => consent.description)}
        renderItem={renderType}
      />
    ) : t('COMMON.MISSING');
  };

  const renderDate = (date) => (
    <List.Item style={{ display: 'block' }}>
      {moment.utc(date).format('YYYY-MM-DD HH:mm')}
    </List.Item>
  );

  const renderDates = (consents) => {
    const filtered = consents && consents.filter((consent) => !hiddenTypes.includes(consent.type));
    return filtered && filtered.length ? (
      <List
        dataSource={filtered.map((consent) => consent.createdAt)}
        renderItem={renderDate}
      />
    ) : t('COMMON.MISSING');
  };

  useEffect(() => {
    const handleResize = (ev) => setWindowHeight(ev.currentTarget.innerHeight);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const columns = [
    {
      title: t('CARRIERS.RECIPIENT'),
      dataIndex: 'recipient',
      key: 'recipient',
      width: '25%',
      render: (_, { recipient }) => <RecipientInfo recipient={recipient} />,
    },
    {
      title: t('CARRIERS.CONSENT_TYPE'),
      dataIndex: 'consents',
      key: 'types',
      width: '35%',
      render: (_, { consents }) => renderTypes(consents),
    },
    {
      title: t('CARRIERS.CONSENT_GIVEN_AT'),
      dataIndex: 'consents',
      key: 'createdAt',
      width: '15%',
      render: (_, { consents }) => renderDates(consents),
    },
  ];
  if (isPredefinedLocationConsentEnabled) {
    columns.push({
      title: t('CARRIERS.PREDEFINED_LOCATION_ACCESS'),
      key: 'action',
      width: '25%',
      shouldCellUpdate: (record, prevRecord) => (
        getConsentsKey(record.consents) !== getConsentsKey(prevRecord.consents)
      ),
      render: (_, { recipient, consents }) => (
        <PredefinedLocationAccessCell
          recipient={recipient}
          consents={consents}
          predefinedType={predefinedType}
        />
      ),
    });
  }

  return (
    <StyledTable
      scroll={{ y: Math.max(200, windowHeight - 290) }}
      dataSource={givenConsents?.data}
      rowKey={(record) => {
        const baseKey = record.recipient?.id || record.recipient?.taxNumber || record.recipient?.name;
        return `${baseKey}-${getConsentsKey(record.consents)}`;
      }}
      size={size}
      bordered
      pagination={false}
      columns={columns}
      loading={TableLoadingComponent(givenConsents?.loading)}
    />
  );
};

GivenConsentsTable.propTypes = {
  size: PropTypes.oneOf(['small', 'medium', 'large']),
};

GivenConsentsTable.defaultProps = {
  size: 'small',
};

export default GivenConsentsTable;
